import React, { useEffect, useState } from "react";
import { LEGEND_PORTRAITS, legendPortrait, LEGEND_WEAPONS, ALL_WEAPONS, capitalize, roleIcons } from "./legendData";

// ─── Types ────────────────────────────────────────────────────────────────────
type Role = "Founder" | "Officer" | "Member" | "Recruit";

type Member = {
  id:           number;
  tag:          string;
  discordId:    string;
  brawlhallaId: string;
  role:         Role;
  joinDate:     string;
  notes:        string;
  nameHistory?: string[];
};

type LegendStat = {
  legend_id:         number;
  legend_name_key:   string;
  damagedealt:       string;
  damagetaken:       string;
  kos:               number;
  falls:             number;
  suicides:          number;
  teamkos:           number;
  games:             number;
  wins:              number;
  level:             number;
  xp:                number;
  xp_percentage:     number;
  damageweaponone:   string;
  damageweapontwo:   string;
  damageunarmed:     string;
  damagethrownitem:  string;
  damagegadgets:     string;
  kounarmed:         number;
  kothrownitem:      number;
  koweaponone:       number;
  koweapontwo:       number;
  kogadgets:         number;
  timeheldweaponone: number;
  timeheldweapontwo: number;
  matchtime:         number;
};

type RankedLegend = {
  legend_id:       number;
  legend_name_key: string;
  rating:          number;
  peak_rating:     number;
  tier:            string;
  wins:            number;
  games:           number;
};

type Ranked2v2 = {
  brawlhalla_id_one: number;
  brawlhalla_id_two: number;
  rating:            number;
  peak_rating:       number;
  tier:              string;
  wins:              number;
  games:             number;
  teamname:          string;
  region:            number;
  global_rank:       number;
};

type PlayerStats = {
  brawlhalla_id:   number;
  name:            string;
  xp:              number;
  level:           number;
  xp_percentage:   number;
  games:           number;
  wins:            number;
  damagebomb:      string;
  damagemine:      string;
  damagespikeball: string;
  damagesidekick:  string;
  hitsnowball:     number;
  kobomb:          number;
  komine:          number;
  kospikeball:     number;
  kosidekick:      number;
  kosnowball:      number;
  legends:         LegendStat[];
  clan?: {
    clan_name:   string;
    clan_id:     number;
    clan_xp:     string;
    personal_xp: number;
  };
};

type PlayerRanked = {
  name:          string;
  brawlhalla_id: number;
  rating:        number;
  peak_rating:   number;
  tier:          string;
  wins:          number;
  games:         number;
  region:        string;
  global_rank:   number;
  region_rank:   number;
  legends:       RankedLegend[];
  "2v2":         Ranked2v2[];
};

type BhApiState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; stats: PlayerStats; ranked: PlayerRanked };

type ProfileTab = "overview" | "2v2" | "legends" | "weapons";

type DiscordUser = {
  id:          string;
  username:    string;
  globalName:  string;
  avatar:      string;
  banner:      string | null;
  accentColor: number | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateString: string): string {
  if (!dateString) return "—";
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
}

function winRate(wins: number, games: number): string {
  if (!games) return "0%";
  return `${Math.round((wins / games) * 100)}%`;
}

function tierColor(tier: string): string {
  const t = tier.toLowerCase();
  if (t.includes("diamond"))  return "#b9f2ff";
  if (t.includes("platinum")) return "#00cfff";
  if (t.includes("gold"))     return "#ffd700";
  if (t.includes("silver"))   return "#c0c0c0";
  if (t.includes("bronze"))   return "#cd7f32";
  return "#a1a1aa";
}

function tierImage(tier: string): string {
  const t = tier.toLowerCase();
  if (t.includes("diamond"))   return "/ranks/diamond.png";
  if (t.includes("platinum"))  return "/ranks/plat.png";
  if (t.includes("gold"))      return "/ranks/gold.png";
  if (t.includes("silver"))    return "/ranks/silver.png";
  if (t.includes("bronze"))    return "/ranks/bronze.png";
  if (t.includes("valhallan")) return "/ranks/valhallan.png";
  return "/ranks/tin.png";
}

function fmtNum(n: number | string): string {
  return Number(n).toLocaleString();
}

function formatPlaytime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h.toLocaleString()}h ${m}m`;
  return `${m}m`;
}

function damageRatio(dealt: string | number, taken: string | number): string {
  const d = Number(dealt);
  const t = Number(taken);
  if (!t) return "—";
  return (d / t).toFixed(2);
}

function eloReset(rating: number): number {
  if (rating < 1400) return rating;
  return Math.round((rating + 375) / 1.5);
}

// ─── Styles ───────────────────────────────────────────────────────────────────
function ProfileStyles() {
  return (
    <style>{`
      .profile-page { max-width: 1100px; margin: 0 auto; }

      .profile-hero {
        background: var(--bg-card);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        margin-bottom: 16px;
        overflow: hidden;
        text-align: center;
      }

      /* Banner */
      .profile-hero__banner {
        width: 100%;
        height: 140px;
        object-fit: cover;
        display: block;
      }
      .profile-hero__banner-placeholder {
        width: 100%;
        height: 60px;
      }

      /* Avatar row */
      .profile-hero__avatar-row {
        display: flex;
        justify-content: center;
        gap: 12px;
        margin-top: -30px;
        margin-bottom: 14px;
        padding: 0 36px;
        position: relative;
        z-index: 1;
      }

      .profile-hero__discord-avatar {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        border: 3px solid var(--bg-card);
        object-fit: cover;
        background: rgba(255,255,255,0.04);
        flex-shrink: 0;
      }

      .profile-hero__role-icon {
        width: 52px;
        height: 52px;
        border-radius: 14px;
        border: 2px solid var(--bg-card);
        background: rgba(255,255,255,0.04);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        align-self: flex-end;
      }

      .profile-hero__avatar-fallback {
        width: 72px;
        height: 72px;
        border-radius: 18px;
        background: rgba(255,255,255,0.04);
        border: 1px solid var(--border);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 14px;
      }

      .profile-hero__body { padding: 0 36px 36px; }

      .profile-hero__name { font-size: 62px; font-weight: 800; line-height: 1.0; margin: 0 0 10px 0; }

      .profile-hero__prev-names {
        display: flex; justify-content: center; gap: 10px; flex-wrap: wrap;
        margin-bottom: 14px; min-height: 20px;
      }

      .prev-name {
        font-size: 13px; color: var(--text-muted);
        background: rgba(255,255,255,0.04); border: 1px solid var(--border);
        border-radius: 6px; padding: 2px 10px;
      }

      .prev-name__arrow { color: rgba(255,255,255,0.2); font-size: 11px; margin: 0 2px; }

      .profile-hero__chips { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 24px; }

      .profile-hero__info-row {
        display: flex; justify-content: center; gap: 0; flex-wrap: wrap;
        border-top: 1px solid var(--border); padding-top: 28px;
      }

      .profile-info-item { padding: 0 32px; border-right: 1px solid var(--border); text-align: center; }
      .profile-info-item:last-child { border-right: none; }
      .profile-info-item:first-child { padding-left: 0; }
      .profile-info-item__label { font-size: 12px; color: var(--text-muted); font-weight: 400; letter-spacing: 0.03em; margin-bottom: 6px; }
      .profile-info-item__value { font-size: 22px; font-weight: 800; }
      .profile-info-item__sub { font-size: 11px; color: var(--text-muted); margin-top: 3px; }

      .tier-badge {
        display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px;
        border-radius: var(--radius-full); border: 1px solid rgba(255,255,255,0.1);
        background: rgba(255,255,255,0.05); font-size: 13px; font-weight: 700;
      }
      .tier-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

      .profile-tabs { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }

      .profile-tab {
        padding: 12px 28px; border-radius: var(--radius-sm); border: 1px solid var(--border);
        background: transparent; color: var(--text-muted); font-size: 14px; font-weight: 600;
        letter-spacing: 0.03em; cursor: pointer; font-family: var(--font-body); transition: all 0.15s;
      }
      .profile-tab:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }
      .profile-tab.active { background: var(--accent); color: #000; border-color: var(--accent); }

      .p-card { background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 22px; margin-bottom: 16px; }
      .p-card__label { color: var(--accent); font-size: 12px; font-weight: 600; letter-spacing: 0.03em; margin-bottom: 14px; }

      .p-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }

      .p-stat { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: var(--radius-sm); padding: 20px 18px; }
      .p-stat__label { color: var(--text-muted); font-size: 12px; font-weight: 400; letter-spacing: 0.03em; margin-bottom: 8px; }
      .p-stat__value { font-size: 28px; font-weight: 800; line-height: 1.1; }
      .p-stat__value--accent { color: var(--accent); }
      .p-stat__value--danger { color: var(--danger); }
      .p-stat__sub { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

      .ranked-content-box { display: flex; align-items: center; gap: 12px; margin-top: 28px; }

      .ranked-hero__img { width: 150px; height: 150px; object-fit: contain; flex-shrink: 0; filter: drop-shadow(0 0 12px rgba(255,255,255,0.1)); }
      .ranked-hero__img--glow-diamond  { animation: glow-diamond  2.5s ease-in-out infinite; }
      .ranked-hero__img--glow-platinum { animation: glow-platinum 2.5s ease-in-out infinite; }
      .ranked-hero__img--glow-gold     { animation: glow-gold     2.5s ease-in-out infinite; }
      .ranked-hero__img--glow-silver   { animation: glow-silver   2.5s ease-in-out infinite; }
      .ranked-hero__img--glow-bronze   { animation: glow-bronze   2.5s ease-in-out infinite; }

      @keyframes glow-diamond  { 0%,100% { filter: drop-shadow(0 0 8px rgba(185,242,255,0.3));  } 50% { filter: drop-shadow(0 0 22px rgba(185,242,255,0.85)); } }
      @keyframes glow-platinum { 0%,100% { filter: drop-shadow(0 0 8px rgba(0,207,255,0.3));    } 50% { filter: drop-shadow(0 0 22px rgba(0,207,255,0.85));   } }
      @keyframes glow-gold     { 0%,100% { filter: drop-shadow(0 0 8px rgba(255,215,0,0.3));    } 50% { filter: drop-shadow(0 0 22px rgba(255,215,0,0.85));   } }
      @keyframes glow-silver   { 0%,100% { filter: drop-shadow(0 0 8px rgba(192,192,192,0.3));  } 50% { filter: drop-shadow(0 0 22px rgba(192,192,192,0.75)); } }
      @keyframes glow-bronze   { 0%,100% { filter: drop-shadow(0 0 8px rgba(205,127,50,0.3));   } 50% { filter: drop-shadow(0 0 22px rgba(205,127,50,0.75));  } }
      .ranked-hero__right { display: flex; flex-direction: column; gap: 2px; }
      .ranked-hero__current { font-size: 36px; font-weight: 800; line-height: 1; color: var(--text); }
      .ranked-hero__sep { font-size: 36px; font-weight: 800; color: var(--text); }
      .ranked-hero__peak { font-size: 36px; font-weight: 800; color: var(--text); }

      .wl-bar-wrap { margin-bottom: 12px; width: 260px; }
      .wl-bar { height: 7px; border-radius: 99px; overflow: hidden; display: flex; margin-bottom: 6px; width: 260px; }
      .wl-bar__wins { background: var(--accent); height: 100%; transition: width 0.5s ease; }
      .wl-bar__losses { background: var(--danger); height: 100%; flex: 1; }
      .wl-bar__labels { display: flex; justify-content: space-between; width: 280px; font-size: 13px; font-weight: 700; }
      .wl-bar__wins-label { color: var(--accent); }
      .wl-bar__losses-label { color: var(--danger); }

      .ranked-stats-row { display: flex; gap: 32px; flex-wrap: wrap; padding-top: 16px; border-top: 1px solid var(--border); }
      .ranked-stat-item__label { font-size: 12px; color: var(--text-muted); font-weight: 400; margin-bottom: 4px; }
      .ranked-stat-item__value { font-size: 22px; font-weight: 800; }

      .ratio-section { margin-top: 20px; }
      .ratio-bar-wrap { margin: 8px 0; }
      .ratio-bar { height: 8px; background: rgba(248,113,113,0.25); border-radius: 99px; overflow: hidden; }
      .ratio-bar__fill { height: 100%; background: var(--accent); border-radius: 99px; transition: width 0.6s ease; }
      .ratio-bar__labels { display: flex; justify-content: space-between; font-size: 13px; margin-top: 6px; }

      .gadget-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
      .gadget-box { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: var(--radius-sm); padding: 20px; }
      .gadget-box__name { font-size: 13px; color: var(--text-muted); font-weight: 400; letter-spacing: 0.03em; margin-bottom: 8px; }
      .gadget-box__kos { font-size: 32px; font-weight: 800; margin-bottom: 6px; }
      .gadget-box__detail { font-size: 12px; color: var(--text-muted); }

      .twos-summary-grid { display: flex; flex-wrap: wrap; gap: 26px 40px; margin-bottom: 22px; }
      .twos-summary-item__label { font-size: 12px; color: var(--text-muted); font-weight: 400; margin-bottom: 6px; }
      .twos-summary-item__value { font-size: 20px; font-weight: 800; }

      .twos-controls { display: flex; flex-direction: column; gap: 4px; margin-bottom: 20px; max-width: 340px; }
      .twos-controls__label { font-size: 11px; color: var(--text-muted); font-weight: 500; letter-spacing: 0.04em; }

      .twos-cards-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
      .twos-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: var(--radius-sm); padding: 20px; }
      .twos-card__team { font-size: 13px; font-weight: 600; color: var(--text-muted); margin-bottom: 10px; }
      .twos-card__rating-row { display: flex; align-items: baseline; gap: 6px; margin-bottom: 4px; }
      .twos-card__rating { font-size: 30px; font-weight: 800; }
      .twos-card__sep { font-size: 20px; font-weight: 700; color: var(--text-muted); }
      .twos-card__peak { font-size: 20px; font-weight: 700; color: var(--text-muted); }
      .twos-card__peak-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.05em; color: var(--text-muted); margin-left: 2px; }

      .twos-card__bar { height: 6px; border-radius: 99px; overflow: hidden; display: flex; margin: 12px 0 6px; width: 100%; }
      .twos-card__bar-wins { background: var(--accent); height: 100%; }
      .twos-card__bar-losses { background: var(--danger); height: 100%; flex: 1; }
      .twos-card__bar-labels { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; }
      .twos-card__bar-labels .win { color: var(--accent); }
      .twos-card__bar-labels .loss { color: var(--danger); }

      .twos-card__stats-row { display: flex; justify-content: space-between; padding-top: 14px; margin-top: 12px; border-top: 1px solid rgba(255,255,255,0.05); }
      .twos-stat { text-align: left; }
      .twos-stat__label { font-size: 11px; color: var(--text-muted); font-weight: 400; letter-spacing: 0.03em; display: block; margin-bottom: 3px; }
      .twos-stat__value { font-size: 15px; font-weight: 700; }

      .legend-sub-tabs { display: flex; gap: 8px; margin-bottom: 16px; }
      .legend-sub-tab {
        padding: 6px 16px; border-radius: var(--radius-sm); border: 1px solid var(--border);
        background: transparent; color: var(--text-muted); font-size: 12px; font-weight: 600;
        letter-spacing: 0.03em; cursor: pointer; font-family: var(--font-body); transition: all 0.15s;
      }
      .legend-sub-tab:hover { color: var(--text); }
      .legend-sub-tab.active { background: rgba(57,217,138,0.15); color: var(--accent); border-color: rgba(57,217,138,0.3); }

      .tbl-wrap { overflow-x: auto; }

      table.p-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      table.p-table th {
        padding: 10px 14px; text-align: left; color: var(--text-muted); font-size: 12px;
        font-weight: 500; letter-spacing: 0.03em; border-bottom: 1px solid var(--border);
        cursor: pointer; user-select: none; white-space: nowrap;
      }
      table.p-table th:hover { color: var(--text); }
      table.p-table th.sort-active { color: var(--accent); }
      table.p-table td { padding: 11px 14px; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle; }
      table.p-table tr:last-child td { border-bottom: none; }
      table.p-table tr:hover td { background: rgba(255,255,255,0.02); }

      .legend-name-cell { display: flex; align-items: center; gap: 10px; }
      .legend-portrait { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; flex-shrink: 0; background: rgba(255,255,255,0.04); }
      .tbl-legend-name { font-weight: 700; font-size: 14px; }
      .tbl-legend-level { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

      .xp-mini { height: 3px; background: rgba(255,255,255,0.08); border-radius: 99px; overflow: hidden; margin-top: 4px; width: 70px; }
      .xp-mini__fill { height: 100%; background: var(--accent); border-radius: 99px; }

      .wr-pill { display: inline-block; padding: 2px 8px; border-radius: 99px; font-size: 12px; font-weight: 700; background: rgba(57,217,138,0.1); color: var(--accent); }
      .wr-pill--low { background: rgba(248,113,113,0.1); color: var(--danger); }

      .weapon-row { display: grid; grid-template-columns: 180px repeat(5, 1fr); align-items: center; padding: 14px 0; border-bottom: 1px solid rgba(255,255,255,0.05); gap: 8px; }
      .weapon-row:last-child { border-bottom: none; }
      .weapon-row:first-child { padding-top: 0; }
      .weapon-row__name { font-size: 15px; font-weight: 700; }
      .weapon-stat { text-align: center; }
      .weapon-stat__label { font-size: 12px; color: var(--text-muted); font-weight: 400; letter-spacing: 0.03em; margin-bottom: 4px; }
      .weapon-stat__value { font-size: 16px; font-weight: 700; }
      .weapon-bar-wrap { margin-top: 6px; }
      .weapon-bar { height: 4px; background: rgba(255,255,255,0.08); border-radius: 99px; overflow: hidden; }
      .weapon-bar__fill { height: 100%; background: var(--accent); border-radius: 99px; }

      .legend-accordion { border-bottom: 1px solid rgba(255,255,255,0.05); }
      .legend-accordion:last-child { border-bottom: none; }
      .legend-accordion__row { display: flex; align-items: center; padding: 10px 8px; cursor: pointer; border-radius: var(--radius-sm); transition: background 0.1s; gap: 12px; }
      .legend-accordion__row:hover { background: rgba(255,255,255,0.03); }
      .legend-accordion__row.open { background: rgba(57,217,138,0.04); }
      .legend-accordion__chevron { font-size: 10px; color: var(--text-muted); flex-shrink: 0; }
      .legend-accordion__panel { padding: 16px 12px 20px; border-top: 1px solid rgba(255,255,255,0.05); display: flex; flex-direction: column; gap: 12px; }

      .legend-expand__section-label { font-size: 11px; font-weight: 600; color: var(--accent); letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 2px; margin-top: 4px; }
      .legend-expand__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 8px; }
      .legend-expand__stat { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 10px 12px; }
      .legend-expand__stat-label { font-size: 11px; color: var(--text-muted); font-weight: 400; margin-bottom: 4px; }
      .legend-expand__stat-value { font-size: 16px; font-weight: 700; }

      .profile-loading { display: flex; align-items: center; gap: 12px; color: var(--text-muted); padding: 80px 0; font-size: 15px; justify-content: center; }
      .profile-loading__spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.1); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
      @keyframes spin { to { transform: rotate(360deg); } }
      .profile-error { color: var(--danger); padding: 16px; background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2); border-radius: var(--radius-md); font-size: 14px; }
    `}</style>
  );
}

// ─── Small components ─────────────────────────────────────────────────────────
function TierBadge({ tier }: { tier: string }) {
  return (
    <span className="tier-badge" style={{ color: tierColor(tier) }}>
      <span className="tier-dot" style={{ background: tierColor(tier) }} />
      {tier}
    </span>
  );
}

function WrPill({ wins, games }: { wins: number; games: number }) {
  const wr = games ? wins / games : 0;
  return <span className={`wr-pill${wr < 0.45 ? " wr-pill--low" : ""}`}>{winRate(wins, games)}</span>;
}

// ─── Profile Content ──────────────────────────────────────────────────────────
function ProfileContent({ member, stats, ranked, discordUser, tab, onTabChange }: {
  member:      Member;
  stats:       PlayerStats;
  ranked:      PlayerRanked;
  discordUser: DiscordUser | null;
  tab:         ProfileTab;
  onTabChange: (tab: ProfileTab) => void;
}) {
  const [legendSort,     setLegendSort]     = useState<string>("level");
  const [legendDir,      setLegendDir]      = useState<"asc" | "desc">("desc");
  const [expandedLegend, setExpandedLegend] = useState<number | null>(null);
  const [weaponFilter,   setWeaponFilter]   = useState<string>("All Weapons");
  const [twosSort,       setTwosSort]       = useState<string>("elo");
  const [twosDir,        setTwosDir]        = useState<"asc" | "desc">("desc");
  const [expandedWeapon, setExpandedWeapon] = useState<string | null>(null);

  const totalPlaytime = stats.legends.reduce((s, l) => s + (l.matchtime || 0), 0);
  const totalDmgDealt = stats.legends.reduce((s, l) => s + Number(l.damagedealt  || 0), 0);
  const totalDmgTaken = stats.legends.reduce((s, l) => s + Number(l.damagetaken  || 0), 0);
  const totalKos      = stats.legends.reduce((s, l) => s + l.kos, 0);
  const totalFalls    = stats.legends.reduce((s, l) => s + l.falls, 0);
  const losses        = stats.games - stats.wins;

  const rankedByLegend = new Map(ranked.legends.map(l => [l.legend_id, l]));

  const legendsWithWeapons = stats.legends.map(l => ({
    ...l,
    w1dmg:  Number(l.damageweaponone  || 0),
    w2dmg:  Number(l.damageweapontwo  || 0),
    w1kos:  l.koweaponone  || 0,
    w2kos:  l.koweapontwo  || 0,
    w1time: l.timeheldweaponone || 0,
    w2time: l.timeheldweapontwo || 0,
  }));

  const weaponFilteredLegends = weaponFilter === "All Weapons"
    ? stats.legends
    : stats.legends.filter(l => {
        const weapons = LEGEND_WEAPONS[l.legend_name_key.toLowerCase()];
        return weapons && (weapons[0] === weaponFilter || weapons[1] === weaponFilter);
      });

  const sortedLegends = [...weaponFilteredLegends].sort((a, b) => {
    if (legendSort === "legend_name_key") {
      const av = a.legend_name_key.toLowerCase();
      const bv = b.legend_name_key.toLowerCase();
      return legendDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
    }
    let av: number, bv: number;
    if (legendSort === "winrate")   { av = a.games ? a.wins / a.games : 0; bv = b.games ? b.wins / b.games : 0; }
    else if (legendSort === "elo")      { av = rankedByLegend.get(a.legend_id)?.rating ?? 0; bv = rankedByLegend.get(b.legend_id)?.rating ?? 0; }
    else if (legendSort === "peak_elo") { av = rankedByLegend.get(a.legend_id)?.peak_rating ?? 0; bv = rankedByLegend.get(b.legend_id)?.peak_rating ?? 0; }
    else if (legendSort === "losses")   { av = a.games - a.wins; bv = b.games - b.wins; }
    else { const aVal = (a as any)[legendSort]; const bVal = (b as any)[legendSort]; av = typeof aVal === "string" ? Number(aVal) : (aVal ?? 0); bv = typeof bVal === "string" ? Number(bVal) : (bVal ?? 0); }
    if (av === bv && legendSort === "level") return legendDir === "desc" ? b.xp - a.xp : a.xp - b.xp;
    return legendDir === "desc" ? bv - av : av - bv;
  });

  const toggleSort = (field: string) => {
    if (legendSort === field) setLegendDir(d => d === "desc" ? "asc" : "desc");
    else { setLegendSort(field); setLegendDir("desc"); }
  };

  const twos = ranked["2v2"] || [];
  const twosTotalGames    = twos.reduce((s, t) => s + t.games, 0);
  const twosTotalWins     = twos.reduce((s, t) => s + t.wins, 0);
  const twosTotalLosses   = twosTotalGames - twosTotalWins;
  const twosTeammateCount = twos.length;
  const twosAvgRating     = twosTeammateCount ? Math.round(twos.reduce((s, t) => s + t.rating, 0) / twosTeammateCount) : 0;
  const twosAvgPeak       = twosTeammateCount ? Math.round(twos.reduce((s, t) => s + t.peak_rating, 0) / twosTeammateCount) : 0;

  const sortedTwos = [...twos].sort((a, b) => {
    if (twosSort === "teamname") return twosDir === "desc" ? b.teamname.localeCompare(a.teamname) : a.teamname.localeCompare(b.teamname);
    let av: number, bv: number;
    if (twosSort === "winrate")   { av = a.games ? a.wins / a.games : 0; bv = b.games ? b.wins / b.games : 0; }
    else if (twosSort === "losses")   { av = a.games - a.wins; bv = b.games - b.wins; }
    else if (twosSort === "elo")      { av = a.rating; bv = b.rating; }
    else if (twosSort === "peak_elo") { av = a.peak_rating; bv = b.peak_rating; }
    else { av = (a as any)[twosSort] ?? 0; bv = (b as any)[twosSort] ?? 0; }
    return twosDir === "desc" ? bv - av : av - bv;
  });

  const maxW1Dmg = Math.max(...legendsWithWeapons.map(l => l.w1dmg), 1);
  const maxW2Dmg = Math.max(...legendsWithWeapons.map(l => l.w2dmg), 1);
  const dmgTotal = totalDmgDealt + totalDmgTaken;
  const dmgPct   = dmgTotal ? Math.round((totalDmgDealt / dmgTotal) * 100) : 50;
  const nameHistory = member.nameHistory || [];

  // Banner background color fallback
  const bannerBg = discordUser?.accentColor
    ? `#${discordUser.accentColor.toString(16).padStart(6, "0")}`
    : "rgba(255,255,255,0.03)";

  return (
    <>
      <div className="profile-hero">
        {/* Banner */}
        {discordUser?.banner ? (
          <img src={discordUser.banner} alt="banner" className="profile-hero__banner" />
        ) : (
          <div className="profile-hero__banner-placeholder" style={{ background: bannerBg }} />
        )}

        {/* Avatar row — Discord avatar + role icon */}
        <div className="profile-hero__avatar-row">
          {discordUser?.avatar ? (
            <img src={discordUser.avatar} alt="Discord avatar" className="profile-hero__discord-avatar" />
          ) : (
            <div className="profile-hero__discord-avatar" style={{ background: "rgba(255,255,255,0.04)", border: "3px solid var(--bg-card)" }} />
          )}
          <div className="profile-hero__role-icon">
            <img src={roleIcons[member.role]} alt={member.role} style={{ width: 32, height: 32, objectFit: "contain" }} />
          </div>
        </div>

        <div className="profile-hero__body">
          <h1 className="profile-hero__name">{stats.name}</h1>
          {nameHistory.length > 0 && (
            <div className="profile-hero__prev-names">
              {nameHistory.map((name, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="prev-name__arrow">←</span>}
                  <span className="prev-name">{name}</span>
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="profile-hero__chips">
            <TierBadge tier={ranked.tier} />
            <span className="chip">{member.role}</span>
            {stats.clan && <span className="chip">{stats.clan.clan_name}</span>}
          </div>
          <div className="profile-hero__info-row">
            <div className="profile-info-item">
              <div className="profile-info-item__label">Brawlhalla ID</div>
              <div className="profile-info-item__value">{member.brawlhallaId || stats.brawlhalla_id}</div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-item__label">Region</div>
              <div className="profile-info-item__value">{ranked.region || "—"}</div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-item__label">Level</div>
              <div className="profile-info-item__value">{stats.level}</div>
              <div className="profile-info-item__sub">{Math.round(stats.xp_percentage * 100)}% to next</div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-item__label">Total XP</div>
              <div className="profile-info-item__value">{fmtNum(stats.xp)}</div>
            </div>
            <div className="profile-info-item">
              <div className="profile-info-item__label">Playtime</div>
              <div className="profile-info-item__value">{formatPlaytime(totalPlaytime)}</div>
            </div>
            {member.joinDate && (
              <div className="profile-info-item">
                <div className="profile-info-item__label">Joined</div>
                <div className="profile-info-item__value">{formatDate(member.joinDate)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        {(["overview", "2v2", "legends", "weapons"] as ProfileTab[]).map(t => (
          <button key={t} className={`profile-tab${tab === t ? " active" : ""}`} onClick={() => onTabChange(t)}>
            {t === "2v2" ? "2v2 Ranked" : capitalize(t)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="p-card">
            <div className="p-card__label" style={{ marginBottom: 20 }}>Ranked 1v1</div>
            <div className="ranked-content-box">
              <img
                  src={tierImage(ranked.tier)}
                  alt={ranked.tier}
                  className={`ranked-hero__img${
                    ranked.tier.toLowerCase().includes("diamond")  ? " ranked-hero__img--glow-diamond"  :
                    ranked.tier.toLowerCase().includes("platinum") ? " ranked-hero__img--glow-platinum" :
                    ranked.tier.toLowerCase().includes("gold")     ? " ranked-hero__img--glow-gold"     :
                    ranked.tier.toLowerCase().includes("silver")   ? " ranked-hero__img--glow-silver"   :
                    ranked.tier.toLowerCase().includes("bronze")   ? " ranked-hero__img--glow-bronze"   : ""
                  }`}
                />
              <div className="ranked-hero__right">
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "table" }}>
                      <div style={{ display: "table-row" }}>
                        <span style={{ display: "table-cell", textAlign: "center", paddingRight: 8 }} className="ranked-hero__current">{ranked.rating}</span>
                        <span style={{ display: "table-cell", textAlign: "center", paddingRight: 8 }} className="ranked-hero__sep">/</span>
                        <span style={{ display: "table-cell", textAlign: "center" }} className="ranked-hero__peak">{ranked.peak_rating}</span>
                      </div>
                      <div style={{ display: "table-row" }}>
                        <span style={{ display: "table-cell", textAlign: "center", fontSize: 11, color: "var(--text-muted)", fontWeight: 400, letterSpacing: "0.05em", textTransform: "uppercase", paddingTop: 3, paddingRight: 8 }}>Current</span>
                        <span style={{ display: "table-cell" }} />
                        <span style={{ display: "table-cell", textAlign: "center", fontSize: 11, color: "var(--text-muted)", fontWeight: 400, letterSpacing: "0.05em", textTransform: "uppercase", paddingTop: 3 }}>Peak</span>
                      </div>
                    </div>
                  </div>
                  {ranked.games > 0 && (
                    <div className="wl-bar-wrap">
                      <div className="wl-bar">
                        <div className="wl-bar__wins" style={{ width: `${Math.round((ranked.wins / ranked.games) * 100)}%` }} />
                        <div className="wl-bar__losses" />
                      </div>
                      <div className="wl-bar__labels">
                        <span className="wl-bar__wins-label">{ranked.wins}W ({winRate(ranked.wins, ranked.games)})</span>
                        <span style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 400, marginTop: 4, display: "inline-block" }}>{ranked.games} games</span>
                        <span className="wl-bar__losses-label">{ranked.games - ranked.wins}L ({winRate(ranked.games - ranked.wins, ranked.games)})</span>
                      </div>
                    </div>
                  )}
                </div>
                {(ranked.region_rank > 0 || ranked.global_rank > 0) && (
                  <div className="ranked-stats-row">
                    {ranked.region_rank > 0 && (
                      <div className="ranked-stat-item">
                        <div className="ranked-stat-item__label">Region rank</div>
                        <div className="ranked-stat-item__value">#{fmtNum(ranked.region_rank)}</div>
                      </div>
                    )}
                    {ranked.global_rank > 0 && (
                      <div className="ranked-stat-item">
                        <div className="ranked-stat-item__label">Global rank</div>
                        <div className="ranked-stat-item__value">#{fmtNum(ranked.global_rank)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-card">
            <div className="p-card__label">Overall Stats</div>
            <div className="p-grid">
              <div className="p-stat"><div className="p-stat__label">Games</div><div className="p-stat__value">{fmtNum(stats.games)}</div></div>
              <div className="p-stat"><div className="p-stat__label">Wins</div><div className="p-stat__value">{fmtNum(stats.wins)}</div></div>
              <div className="p-stat"><div className="p-stat__label">Losses</div><div className="p-stat__value">{fmtNum(losses)}</div></div>
              <div className="p-stat"><div className="p-stat__label">Win Rate</div><div className="p-stat__value">{winRate(stats.wins, stats.games)}</div></div>
              <div className="p-stat"><div className="p-stat__label">Total KOs</div><div className="p-stat__value">{fmtNum(totalKos)}</div></div>
              <div className="p-stat"><div className="p-stat__label">Total Falls</div><div className="p-stat__value">{fmtNum(totalFalls)}</div></div>
              <div className="p-stat"><div className="p-stat__label">KO / Fall</div><div className="p-stat__value">{(totalKos / (totalFalls || 1)).toFixed(2)}</div></div>
              <div className="p-stat">
                <div className="p-stat__label">Dmg Ratio</div>
                <div className="p-stat__value">{damageRatio(totalDmgDealt, totalDmgTaken)}</div>
                <div className="p-stat__sub">dealt / taken</div>
              </div>
              {stats.clan && (
                <div className="p-stat">
                  <div className="p-stat__label">Clan XP</div>
                  <div className="p-stat__value">{fmtNum(stats.clan.personal_xp)}</div>
                  <div className="p-stat__sub">{stats.clan.clan_name}</div>
                </div>
              )}
            </div>
            <div className="ratio-section">
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>Damage dealt vs taken</div>
              <div className="ratio-bar-wrap">
                <div className="ratio-bar"><div className="ratio-bar__fill" style={{ width: `${dmgPct}%` }} /></div>
                <div className="ratio-bar__labels">
                  <span style={{ color: "var(--accent)", fontWeight: 700 }}>Dealt {fmtNum(totalDmgDealt)}</span>
                  <span style={{ color: "var(--danger)", fontWeight: 700 }}>Taken {fmtNum(totalDmgTaken)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-card">
            <div className="p-card__label">Gadgets & Throwables</div>
            <div className="gadget-grid">
              {[
                { name: "Bomb",      kos: stats.kobomb,      dmg: stats.damagebomb,      extra: null },
                { name: "Mine",      kos: stats.komine,      dmg: stats.damagemine,      extra: null },
                { name: "Spikeball", kos: stats.kospikeball, dmg: stats.damagespikeball, extra: null },
                { name: "Sidekick",  kos: stats.kosidekick,  dmg: stats.damagesidekick,  extra: null },
                { name: "Snowball",  kos: stats.kosnowball,  dmg: null,                  extra: `${fmtNum(stats.hitsnowball)} hits` },
              ].map(g => (
                <div key={g.name} className="gadget-box">
                  <div className="gadget-box__name">{g.name}</div>
                  <div className="gadget-box__kos">{g.kos} KOs</div>
                  <div className="gadget-box__detail">{g.dmg ? `${fmtNum(g.dmg)} dmg` : g.extra}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === "2v2" && (
        <div className="p-card">
          <div className="p-card__label">Ranked 2v2</div>
          {twos.length === 0 ? (
            <div style={{ color: "var(--text-muted)", padding: "20px 0" }}>No 2v2 data available.</div>
          ) : (
            <>
              <div className="twos-summary-grid">
                <div><div className="twos-summary-item__label">Total games</div><div className="twos-summary-item__value">{fmtNum(twosTotalGames)}</div></div>
                <div><div className="twos-summary-item__label">Total wins</div><div className="twos-summary-item__value">{fmtNum(twosTotalWins)}</div></div>
                <div><div className="twos-summary-item__label">Total losses</div><div className="twos-summary-item__value">{fmtNum(twosTotalLosses)}</div></div>
                <div><div className="twos-summary-item__label">Winrate</div><div className="twos-summary-item__value">{winRate(twosTotalWins, twosTotalGames)}</div></div>
                <div><div className="twos-summary-item__label">Teammates</div><div className="twos-summary-item__value">{twosTeammateCount}</div></div>
                <div><div className="twos-summary-item__label">Avg. games per teammate</div><div className="twos-summary-item__value">{(twosTotalGames / twosTeammateCount).toFixed(2)}</div></div>
                <div><div className="twos-summary-item__label">Avg. wins per teammate</div><div className="twos-summary-item__value">{(twosTotalWins / twosTeammateCount).toFixed(2)}</div></div>
                <div><div className="twos-summary-item__label">Avg. losses per teammate</div><div className="twos-summary-item__value">{(twosTotalLosses / twosTeammateCount).toFixed(2)}</div></div>
                <div><div className="twos-summary-item__label">Avg. team rating</div><div className="twos-summary-item__value">{fmtNum(twosAvgRating)}</div></div>
                <div><div className="twos-summary-item__label">Avg. team peak rating</div><div className="twos-summary-item__value">{fmtNum(twosAvgPeak)}</div></div>
              </div>
              <div className="twos-controls">
                <div className="twos-controls__label">Sort by</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <select className="input" value={twosSort} onChange={e => { setTwosSort(e.target.value); setTwosDir("desc"); }} style={{ flex: 1, fontSize: 13 }}>
                    <option value="elo">Elo</option>
                    <option value="peak_elo">Peak Elo</option>
                    <option value="games">Games</option>
                    <option value="wins">Wins</option>
                    <option value="losses">Losses</option>
                    <option value="winrate">Winrate</option>
                    <option value="teamname">Team Name</option>
                  </select>
                  <button onClick={() => setTwosDir(d => d === "desc" ? "asc" : "desc")} style={{ width: 34, height: 34, flexShrink: 0, background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 14 }}>
                    {twosDir === "desc" ? "↓" : "↑"}
                  </button>
                </div>
              </div>
              <div className="twos-cards-grid">
                {sortedTwos.map((team, i) => (
                  <div key={i} className="twos-card">
                    <div className="twos-card__team">{team.teamname}</div>
                    <div className="twos-card__rating-row">
                      <span className="twos-card__rating">{team.rating}</span>
                      <span className="twos-card__sep">/</span>
                      <span className="twos-card__peak">{team.peak_rating}</span>
                      <span className="twos-card__peak-tag">PEAK</span>
                    </div>
                    {team.games > 0 && (
                      <>
                        <div className="twos-card__bar">
                          <div className="twos-card__bar-wins" style={{ width: `${Math.round((team.wins / team.games) * 100)}%` }} />
                          <div className="twos-card__bar-losses" />
                        </div>
                        <div className="twos-card__bar-labels">
                          <span className="win">{team.wins}W ({winRate(team.wins, team.games)})</span>
                          <span className="loss">{team.games - team.wins}L ({winRate(team.games - team.wins, team.games)})</span>
                        </div>
                      </>
                    )}
                    <div className="twos-card__stats-row">
                      <div className="twos-stat"><span className="twos-stat__label">Games</span><span className="twos-stat__value">{fmtNum(team.games)}</span></div>
                      <div className="twos-stat"><span className="twos-stat__label">Winrate</span><span className="twos-stat__value">{winRate(team.wins, team.games)}</span></div>
                      <div className="twos-stat"><span className="twos-stat__label">Elo reset</span><span className="twos-stat__value">{fmtNum(eloReset(team.rating))}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "legends" && (
        <div className="p-card">
          <div className="p-card__label">Legends</div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 200px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 500, letterSpacing: "0.04em" }}>Filter by weapon</div>
              <select className="input" value={weaponFilter} onChange={e => { setWeaponFilter(e.target.value); setExpandedLegend(null); }} style={{ fontSize: 13 }}>
                <option value="All Weapons">All Weapons</option>
                {ALL_WEAPONS.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 200px" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 500, letterSpacing: "0.04em" }}>Sort by</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <select className="input" value={legendSort} onChange={e => { setLegendSort(e.target.value); setLegendDir("desc"); setExpandedLegend(null); }} style={{ flex: 1, fontSize: 13 }}>
                  <option value="level">Level / XP</option>
                  <option value="matchtime">Matchtime</option>
                  <option value="games">Games</option>
                  <option value="wins">Wins</option>
                  <option value="losses">Losses</option>
                  <option value="winrate">Winrate</option>
                  <option value="elo">Elo</option>
                  <option value="peak_elo">Peak Elo</option>
                  <option value="legend_name_key">Name</option>
                </select>
                <button onClick={() => setLegendDir(d => d === "desc" ? "asc" : "desc")} style={{ width: 34, height: 34, flexShrink: 0, background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 14 }}>
                  {legendDir === "desc" ? "↓" : "↑"}
                </button>
              </div>
            </div>
          </div>

          <div>
            {sortedLegends.map((l, idx) => {
              const rl = rankedByLegend.get(l.legend_id);
              const isOpen = expandedLegend === l.legend_id;
              const isMaxLevel = l.level >= 100;
              const dmgDealt = Number(l.damagedealt || 0);
              const dmgTaken = Number(l.damagetaken || 0);
              const w1dmg = Number(l.damageweaponone || 0);
              const w2dmg = Number(l.damageweapontwo || 0);

              return (
                <div key={l.legend_id} className="legend-accordion">
                  <div className={`legend-accordion__row${isOpen ? " open" : ""}`} onClick={() => setExpandedLegend(isOpen ? null : l.legend_id)}>
                    <div style={{ width: 28, textAlign: "right", flexShrink: 0, fontSize: 13, color: "var(--text-muted)", fontWeight: 500, marginRight: 4 }}>
                      {legendDir === "desc" ? idx + 1 : sortedLegends.length - idx}
                    </div>
                    <div className="legend-name-cell" style={{ flex: 1 }}>
                      {legendPortrait(l.legend_name_key) && (
                        <img src={legendPortrait(l.legend_name_key)} alt={l.legend_name_key} className="legend-portrait" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                      )}
                      <div>
                        <div className="tbl-legend-name" style={isMaxLevel ? { color: "#f5c842" } : undefined}>{capitalize(l.legend_name_key)}</div>
                        <div className="tbl-legend-level">Lvl {l.level}</div>
                        <div className="xp-mini"><div className="xp-mini__fill" style={{ width: `${Math.round(l.xp_percentage * 100)}%` }} /></div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 24, marginLeft: "auto" }}>
                      <div style={{ textAlign: "right" }}>
                        {legendSort === "level" || legendSort === "legend_name_key" ? (
                          <><div style={{ fontSize: 12, fontWeight: 600 }}>Level {l.level} ({fmtNum(l.xp)} xp)</div><div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{formatPlaytime(l.matchtime || 0)}</div></>
                        ) : legendSort === "matchtime" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Playtime</div><div style={{ fontSize: 14, fontWeight: 700 }}>{formatPlaytime(l.matchtime || 0)}</div></>
                        ) : legendSort === "games" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Games</div><div style={{ fontSize: 14, fontWeight: 700 }}>{fmtNum(l.games)}</div></>
                        ) : legendSort === "wins" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Wins</div><div style={{ fontSize: 14, fontWeight: 700, color: "var(--accent)" }}>{fmtNum(l.wins)}</div></>
                        ) : legendSort === "losses" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Losses</div><div style={{ fontSize: 14, fontWeight: 700, color: "var(--danger)" }}>{fmtNum(l.games - l.wins)}</div></>
                        ) : legendSort === "winrate" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Win Rate</div><div style={{ fontSize: 14, fontWeight: 700 }}><WrPill wins={l.wins} games={l.games} /></div></>
                        ) : legendSort === "elo" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Elo</div><div style={{ fontSize: 14, fontWeight: 700 }}>{rankedByLegend.get(l.legend_id)?.rating ?? "—"}</div></>
                        ) : legendSort === "peak_elo" ? (
                          <><div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Peak Elo</div><div style={{ fontSize: 14, fontWeight: 700 }}>{rankedByLegend.get(l.legend_id)?.peak_rating ?? "—"}</div></>
                        ) : (
                          <><div style={{ fontSize: 12, fontWeight: 600 }}>Level {l.level} ({fmtNum(l.xp)} xp)</div><div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{formatPlaytime(l.matchtime || 0)}</div></>
                        )}
                      </div>
                      <span className="legend-accordion__chevron">{isOpen ? "▲" : "▼"}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="legend-accordion__panel">
                      <div className="legend-expand__section-label">Combat</div>
                      <div className="legend-expand__grid">
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Games</div><div className="legend-expand__stat-value">{fmtNum(l.games)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Wins</div><div className="legend-expand__stat-value" style={{ color: "var(--accent)" }}>{fmtNum(l.wins)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Losses</div><div className="legend-expand__stat-value" style={{ color: "var(--danger)" }}>{fmtNum(l.games - l.wins)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Win Rate</div><div className="legend-expand__stat-value"><WrPill wins={l.wins} games={l.games} /></div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">KOs</div><div className="legend-expand__stat-value">{fmtNum(l.kos)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Falls</div><div className="legend-expand__stat-value">{fmtNum(l.falls)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">KO / Fall</div><div className="legend-expand__stat-value">{(l.kos / (l.falls || 1)).toFixed(2)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Suicides</div><div className="legend-expand__stat-value">{l.suicides || 0}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Team KOs</div><div className="legend-expand__stat-value">{l.teamkos || 0}</div></div>
                      </div>

                      <div className="legend-expand__section-label">Damage</div>
                      <div className="legend-expand__grid">
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Dealt</div><div className="legend-expand__stat-value">{fmtNum(dmgDealt)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Taken</div><div className="legend-expand__stat-value" style={{ color: "var(--danger)" }}>{fmtNum(dmgTaken)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Ratio</div><div className="legend-expand__stat-value">{damageRatio(dmgDealt, dmgTaken)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Unarmed Dmg</div><div className="legend-expand__stat-value">{fmtNum(l.damageunarmed || 0)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Unarmed KOs</div><div className="legend-expand__stat-value">{l.kounarmed || 0}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Gadget Dmg</div><div className="legend-expand__stat-value">{fmtNum(l.damagegadgets || 0)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Gadget KOs</div><div className="legend-expand__stat-value">{l.kogadgets || 0}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Thrown Dmg</div><div className="legend-expand__stat-value">{fmtNum(l.damagethrownitem || 0)}</div></div>
                        <div className="legend-expand__stat"><div className="legend-expand__stat-label">Thrown KOs</div><div className="legend-expand__stat-value">{l.kothrownitem || 0}</div></div>
                      </div>

                      <div className="legend-expand__section-label">Weapons</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {[
                          { label: "Weapon 1", dmg: w1dmg, kos: l.koweaponone || 0, time: l.timeheldweaponone || 0 },
                          { label: "Weapon 2", dmg: w2dmg, kos: l.koweapontwo || 0, time: l.timeheldweapontwo || 0 },
                        ].map(w => (
                          <div key={w.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600, letterSpacing: "0.05em", marginBottom: 8 }}>{w.label}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                              <div><div className="legend-expand__stat-label">Damage</div><div className="legend-expand__stat-value">{fmtNum(w.dmg)}</div></div>
                              <div><div className="legend-expand__stat-label">KOs</div><div className="legend-expand__stat-value">{fmtNum(w.kos)}</div></div>
                              <div><div className="legend-expand__stat-label">Time Held</div><div className="legend-expand__stat-value">{formatPlaytime(w.time)}</div></div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {rl && rl.games > 0 && (
                        <>
                          <div className="legend-expand__section-label">Ranked Season</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                            <img src={tierImage(rl.tier)} alt={rl.tier} style={{ width: 56, height: 56, objectFit: "contain" }} />
                            <div>
                              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>{rl.tier}</div>
                              <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
                                <span style={{ fontSize: 28, fontWeight: 800 }}>{rl.rating}</span>
                                <span style={{ color: "var(--text-muted)" }}>/</span>
                                <span style={{ fontSize: 20, fontWeight: 700, color: "var(--text-muted)" }}>{rl.peak_rating} peak</span>
                              </div>
                              <div className="wl-bar" style={{ width: 200, marginBottom: 6 }}>
                                <div className="wl-bar__wins" style={{ width: `${Math.round((rl.wins / rl.games) * 100)}%` }} />
                                <div className="wl-bar__losses" />
                              </div>
                              <div style={{ display: "flex", gap: 16, fontSize: 12, fontWeight: 600 }}>
                                <span style={{ color: "var(--accent)" }}>{rl.wins}W ({winRate(rl.wins, rl.games)})</span>
                                <span style={{ color: "var(--danger)" }}>{rl.games - rl.wins}L</span>
                                <span style={{ color: "var(--text-muted)" }}>{rl.games} games</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "weapons" && (() => {
        const WEAPON_ICONS: Record<string, string> = {
          "Axe":          "https://cms.brawlhalla.com/c/uploads/2022/03/AxeIcon2_96_1.jpg",
          "Battle Boots": "https://cms.brawlhalla.com/c/uploads/2023/01/boots-1-e1673854748156.png",
          "Blasters":     "https://cms.brawlhalla.com/c/uploads/2022/03/BlasterIcon2_96.jpg",
          "Bow":          "https://cms.brawlhalla.com/c/uploads/2021/05/Bow_Icon.png",
          "Cannon":       "https://cms.brawlhalla.com/c/uploads/2022/06/CannonIcon2_96.jpg",
          "Chakram":      "https://cms.brawlhalla.com/c/uploads/2025/01/ChakramIcon_A.png",
          "Gauntlets":    "https://cms.brawlhalla.com/c/uploads/2022/06/Guantlets_Icon_1.png",
          "Greatsword":   "https://cms.brawlhalla.com/c/uploads/2022/06/Greatsword2_Icon.jpg",
          "Hammer":       "https://cms.brawlhalla.com/c/uploads/2022/03/Hammer_Icon_2.png",
          "Katars":       "https://cms.brawlhalla.com/c/uploads/2022/03/Katar_Icon_2.png",
          "Orb":          "https://cms.brawlhalla.com/c/uploads/2022/06/OrbIcon2_96_2.jpg",
          "Rocket Lance": "https://cms.brawlhalla.com/c/uploads/2021/05/Lance_Icon.png",
          "Scythe":       "https://cms.brawlhalla.com/c/uploads/2022/06/Scythe_Icon_1.png",
          "Spear":        "https://cms.brawlhalla.com/c/uploads/2022/03/SpearIcon2_96_1.jpg",
          "Sword":        "https://cms.brawlhalla.com/c/uploads/2022/06/Sword_Icon_2.png",
        };

        // Aggregate stats per weapon across all legends
        const weaponStatsMap = new Map<string, { damage: number; kos: number; timeHeld: number; games: number; wins: number; legends: string[]; legendDetails: { level: number; xp: number }[]; weaponLevel: number }>();

        for (const l of stats.legends) {
          if (l.games === 0) continue;
          const weapons = LEGEND_WEAPONS[l.legend_name_key.toLowerCase()];
          if (!weapons) continue;

          const pairs = [
            { name: weapons[0], dmg: Number(l.damageweaponone || 0), kos: l.koweaponone || 0, time: l.timeheldweaponone || 0, level: l.level, xp: l.xp },
            { name: weapons[1], dmg: Number(l.damageweapontwo || 0), kos: l.koweapontwo || 0, time: l.timeheldweapontwo || 0, level: l.level, xp: l.xp },
          ];

          for (const w of pairs) {
            if (!w.name) continue;
            const existing = weaponStatsMap.get(w.name) ?? { damage: 0, kos: 0, timeHeld: 0, games: 0, wins: 0, legends: [], legendDetails: [], weaponLevel: 0 };
            existing.damage      += w.dmg;
            existing.kos         += w.kos;
            existing.timeHeld    += w.time;
            existing.games       += l.games;
            existing.wins        += l.wins;
            existing.weaponLevel += l.level;
            if (!existing.legends.includes(capitalize(l.legend_name_key))) {
              existing.legends.push(capitalize(l.legend_name_key));
              existing.legendDetails.push({ level: l.level, xp: l.xp });
            }
            weaponStatsMap.set(w.name, existing);
          }
        }

        const weaponList = Array.from(weaponStatsMap.entries())
          .map(([name, s]) => ({ name, ...s }))
          .filter(w => w.timeHeld > 0)
          .sort((a, b) => b.timeHeld - a.timeHeld);

        const maxTime = Math.max(...weaponList.map(w => w.timeHeld), 1);

        return (
          <div className="p-card">
            <div className="p-card__label">Weapons</div>
            <div>
              {weaponList.map((w, idx) => {
                const pct = Math.round((w.timeHeld / maxTime) * 100);
                const isOpen = expandedWeapon === w.name;
                const avgDmgPerGame = w.games ? Math.round(w.damage / w.games) : 0;
                const avgKosPerGame = w.games ? (w.kos / w.games).toFixed(2) : "0";

                const totalTimeSecs = stats.legends.reduce((s, l) => s + (l.matchtime || 0), 0);
                const usageRateTime  = totalTimeSecs ? ((w.timeHeld / totalTimeSecs) * 100).toFixed(2) : "0";
                const usageRateGames = w.games && stats.games ? ((w.games / stats.games) * 100).toFixed(2) : "0";
                const dps = w.timeHeld ? (w.damage / w.timeHeld).toFixed(2) : "0";
                const avgWepLevel = w.legendDetails.length
                  ? Math.round(w.legendDetails.reduce((s, l) => s + l.level, 0) / w.legendDetails.length)
                  : 0;
                const avgLegendXp = w.legendDetails.length
                  ? Math.round(w.legendDetails.reduce((s, l) => s + l.xp, 0) / w.legendDetails.length)
                  : 0;
                const weaponXp = w.legendDetails.reduce((s, l) => s + l.xp, 0);

                return (
                  <div key={w.name} className="legend-accordion">
                    {/* ── Row ── */}
                    <div
                      className={`legend-accordion__row${isOpen ? " open" : ""}`}
                      onClick={() => setExpandedWeapon(isOpen ? null : w.name)}
                      style={{ padding: "14px 8px" }}
                    >
                      <div style={{ width: 24, textAlign: "right", flexShrink: 0, fontSize: 13, color: "var(--text-muted)", fontWeight: 500, marginRight: 4 }}>{idx + 1}</div>
                      {WEAPON_ICONS[w.name] ? (
                        <img src={WEAPON_ICONS[w.name]} alt={w.name} style={{ width: 36, height: 36, objectFit: "contain", borderRadius: 8, flexShrink: 0, background: "rgba(255,255,255,0.04)" }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.04)", flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, marginLeft: 4 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{w.name}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0, minWidth: 110, marginRight: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{formatPlaytime(w.timeHeld)}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{w.legends.length} legend{w.legends.length !== 1 ? "s" : ""}</div>
                      </div>
                      <span className="legend-accordion__chevron">{isOpen ? "▲" : "▼"}</span>
                    </div>

                    {/* ── Expanded panel ── */}
                    {isOpen && (
                      <div className="legend-accordion__panel">
                        {/* Games + W/L bar */}
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>GAMES</div>
                          <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>{fmtNum(w.games)} <span style={{ fontSize: 16, color: "var(--text-muted)", fontWeight: 400 }}>games</span></div>
                          {w.games > 0 && (
                            <>
                              <div style={{ height: 7, borderRadius: 99, overflow: "hidden", display: "flex", marginBottom: 6 }}>
                                <div style={{ background: "var(--accent)", height: "100%", width: `${Math.round((w.wins / w.games) * 100)}%` }} />
                                <div style={{ background: "var(--danger)", height: "100%", flex: 1 }} />
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
                                <span style={{ color: "var(--accent)" }}>{fmtNum(w.wins)}W ({winRate(w.wins, w.games)})</span>
                                <span style={{ color: "var(--danger)" }}>{fmtNum(w.games - w.wins)}L ({winRate(w.games - w.wins, w.games)})</span>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="legend-expand__section-label">Stats</div>
                        <div className="legend-expand__grid">
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Weapon level</div><div className="legend-expand__stat-value">{fmtNum(w.weaponLevel)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Avg. legend level</div><div className="legend-expand__stat-value">{avgWepLevel}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Weapon XP</div><div className="legend-expand__stat-value">{fmtNum(weaponXp)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Avg. legend XP</div><div className="legend-expand__stat-value">{fmtNum(avgLegendXp)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Time held</div><div className="legend-expand__stat-value">{formatPlaytime(w.timeHeld)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Time held (%)</div><div className="legend-expand__stat-value">{usageRateTime}%</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Usage rate (games)</div><div className="legend-expand__stat-value">{usageRateGames}%</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">KOs</div><div className="legend-expand__stat-value">{fmtNum(w.kos)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Avg. KOs / game</div><div className="legend-expand__stat-value">{avgKosPerGame}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Damage dealt</div><div className="legend-expand__stat-value">{fmtNum(w.damage)}</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">DPS</div><div className="legend-expand__stat-value">{dps} dmg/s</div></div>
                          <div className="legend-expand__stat"><div className="legend-expand__stat-label">Avg. dmg / game</div><div className="legend-expand__stat-value">{fmtNum(avgDmgPerGame)}</div></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ─── Main ProfilePage ─────────────────────────────────────────────────────────
export default function ProfilePage({
  member, bhId, tab = "overview", navigateTo, supabase, USE_SUPABASE,
}: {
  member:       Member | null;
  bhId:         string;
  tab?:         ProfileTab;
  navigateTo:   (path: string) => void;
  supabase:     any;
  USE_SUPABASE: boolean;
}) {
  const [apiState,    setApiState]    = useState<BhApiState>({ status: "idle" });
  const [discordUser, setDiscordUser] = useState<DiscordUser | null>(null);

  useEffect(() => {
    const id = bhId || window.location.pathname.split("/player/")[1]?.split("/")[0] || "";
    if (!id) return;
    let cancelled = false;
    setApiState({ status: "loading" });

    async function fetchStats() {
      try {
        const [statsRes, rankedRes] = await Promise.all([
          fetch(`/api/brawlhalla?path=/player/${id}/stats`,  { mode: "cors" }),
          fetch(`/api/brawlhalla?path=/player/${id}/ranked`, { mode: "cors" }),
        ]);
        if (cancelled) return;
        const [stats, ranked] = await Promise.all([statsRes.json(), rankedRes.json()]);
        if (cancelled) return;
        if (stats.error || ranked.error) throw new Error(stats.error || ranked.error);
        setApiState({ status: "loaded", stats, ranked });
      } catch (err: any) {
        if (!cancelled) setApiState({ status: "error", message: err.message || "Unknown error" });
      }
    }

    fetchStats();
    return () => { cancelled = true; };
  }, [bhId]);

  // Fetch Discord avatar/banner when member's discordId is available
  useEffect(() => {
    if (!member?.discordId) return;
    fetch(`/api/discord-user?id=${member.discordId}`)
      .then(r => r.json())
      .then(data => { if (!data.error) setDiscordUser(data); })
      .catch(() => {});
  }, [member?.discordId]);

  return (
    <>
      <ProfileStyles />
      <div className="profile-page">
        <button onClick={() => navigateTo("/roster")} className="btn btn--secondary mb-18">
          ← Back to Roster
        </button>
        {apiState.status === "loading" || apiState.status === "idle" ? (
          <div className="profile-loading">
            <div className="profile-loading__spinner" />
            Loading player stats...
          </div>
        ) : apiState.status === "error" ? (
          <div className="profile-error">Failed to load stats: {apiState.message}</div>
        ) : !member ? (
          <div className="card">
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Player not found</div>
            <div className="text-muted">No roster member matches that Brawlhalla ID.</div>
          </div>
        ) : (
          <ProfileContent
            member={member}
            stats={apiState.stats}
            ranked={apiState.ranked}
            discordUser={discordUser}
            tab={tab}
            onTabChange={(next) => {
              const base = `/player/${encodeURIComponent(bhId)}`;
              navigateTo(next === "overview" ? base : `${base}/${next}`);
            }}
          />
        )}
      </div>
    </>
  );
}
